import { matchPath } from "react-router-dom";

import { truncate } from "lodash";

import {
  DEFAULT_PDF_CSS_STYLE,
  normalizePrintableHtml
} from "components/pdf-generation-v3/pdf-generation-v3.utils";
import {
  RECIPIENT_DEFAULT_TYPE,
  RECIPIENT_PATIENT_OBJECT_TYPE,
  RECIPIENT_PHYSICIAN_OBJECT_TYPE,
  RECIPIENT_REPLY_TO_TYPE
} from "constants/emails";
import { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { type Physician } from "services/types/physician.type";
import {
  convertHtmlToText,
  formatPhoneNumber,
  getFullName
} from "utils/common";
import { convertSunoPlaceholdersToMergeTags } from "utils/suno-merge-tags.util";
import { EMAIL_REGEX } from "utils/validations.utils";

import {
  getMergeTagValuesMap,
  replaceMergeTagsWithValues
} from "./email-form-merge-tags.utils";
import type { EmailFormValues } from "./email-form.component";
import { type Recipient } from "./recipients-field.component";

/**
 * Whether an address can actually be sent to. The single source of truth for that question across
 * every email surface — a second copy drifts the first time either changes, and then two screens
 * disagree about whether the same recipient is emailable.
 */
export const isSendableEmailAddress = (
  value: string | null | undefined
): boolean => {
  const email = value?.trim() || "";
  return Boolean(email) && Boolean(email.match(EMAIL_REGEX));
};

export const getNormalizedEmails = (value: (Recipient | string)[]) => {
  return value
    .filter((recipient) =>
      isSendableEmailAddress(
        typeof recipient === "string" ? recipient : recipient?.email
      )
    )
    .filter((email, index) => index < 50);
};

export const formatRecipient = (
  value: Patient | Physician | null | undefined
) => {
  if (!value || !value.email) return null;

  if ("avatar" in value) {
    return {
      id: value.id,
      email: value.email!,
      name: getFullName({
        firstName: value?.first_name || "",
        middleName: value?.middle_name,
        preferredName: value?.preferred_name,
        lastName: value?.last_name || ""
      }),
      avatar: value?.photo,
      type: RECIPIENT_PATIENT_OBJECT_TYPE,
      email_bounced_at: value.email_bounced_at,
      email_bounce_detail: value.email_bounce_detail
    } as Recipient;
  } else {
    return {
      id: value.id!,
      email: value.email!,
      avatar: "",
      name: [value.first_name, value.last_name].filter(Boolean).join(" "),
      type: RECIPIENT_PHYSICIAN_OBJECT_TYPE
    } as Recipient;
  }
};

export const getDefaultSubject = (clinicName: string) =>
  `Message from ${clinicName}`;

export interface EmailSender {
  name: string;
  address: string;
}

/**
 * What the email will actually be sent as, mirroring the backend's resolution
 * (`Practice.get_resend_client(clinic.no_reply_email)`): the clinic's own no-reply
 * address, else the practice's. Returns null when neither is configured — the
 * backend rejects such a send outright, so there is nothing truthful to display.
 */
export const getEffectiveSender = (
  clinic: Clinic | null | undefined
): EmailSender | null => {
  if (!clinic) return null;

  const address = clinic.no_reply_email || clinic.practice?.no_reply_email;
  if (!address) return null;

  return {
    name: clinic.display_name || clinic.practice?.name || "",
    address
  };
};

/**
 * What "Replies go to" starts out as: the user's saved default
 * (`user_preferences.default_reply_to_email`) when it is a usable address,
 * else the sending address — which is exactly the pre-preference behaviour.
 */
export const getReplyToPrefill = (
  preferredAddress: string | null | undefined,
  senderAddress: string
) => {
  const preferred = preferredAddress?.trim() ?? "";
  return isSendableEmailAddress(preferred) ? preferred : senderAddress;
};

/**
 * A Reply-To participant is only attached when it changes where replies land.
 * The prefill equals the sending address, so an untouched (or cleared) field
 * produces a request identical to one sent before this feature existed.
 */
export const getReplyToParticipant = (
  replyTo: string | undefined,
  senderAddress: string | undefined
) => {
  const address = replyTo?.trim() ?? "";
  if (!isSendableEmailAddress(address)) return null;
  if (address.toLowerCase() === senderAddress?.trim().toLowerCase())
    return null;

  return { type: RECIPIENT_REPLY_TO_TYPE, address };
};

export const getDefaultValueFromPatient = ({
  currentClinic,
  patient,
  userFullName,
  initialData,
  emailSignature,
  noSignature
}: {
  patient: Patient | undefined;
  currentClinic: Clinic;
  userFullName: string;
  initialData: Partial<EmailFormValues>;
  emailSignature?: string;
  noSignature?: boolean;
}) => {
  const clinicName = getDefaultEmailClinicName(currentClinic, patient);
  const { content, subject, ...restValues } = initialData;

  return {
    recipients: patient?.email
      ? [
          {
            id: patient.id,
            name:
              patient.fullName ||
              getFullName({
                firstName: patient.first_name,
                middleName: patient.middle_name,
                preferredName: patient.preferred_name,
                lastName: patient.last_name
              }),
            email: patient?.email,
            type: RECIPIENT_PATIENT_OBJECT_TYPE
          }
        ]
      : [],
    patient,
    subject: subject || getDefaultSubject(clinicName),
    content: getDefaultEmailContent(
      userFullName,
      content,
      emailSignature,
      noSignature
    ),
    ...restValues
  };
};

export const getDefaultEmailClinicName = (
  clinic?: Clinic,
  patient?: Patient
) => {
  return (
    patient?.preferred_clinic?.display_name ??
    clinic?.display_name ??
    clinic?.practice?.name ??
    ""
  );
};

export const getDefaultEmailContent = (
  userFullName: string,
  content?: string,
  emailSignature?: string,
  noSignature?: boolean
) => {
  if (noSignature) {
    return `<div>${content ? content : "&nbsp;"}</div>`;
  }
  return `<div>${content ? content : "&nbsp;"}</div><div>&nbsp;</div>${
    emailSignature
      ? `<div>${emailSignature}</div>`
      : `<div>Best regards,</div><div>${userFullName}</div>`
  }`;
};

export const getPatientPortalEmailContent = ({
  patientName,
  portalLink
}: {
  patientName: string;
  portalLink: string;
}): string =>
  `<div>Dear ${patientName},</div><div>&nbsp;</div><div>You can login to the <a href="${portalLink}">patient portal</a> to view all your unpaid invoices, and make payments.</div>`;

export const getStatementEmailContent = ({
  patientName
}: {
  patientName: string;
}): string =>
  `<div>Dear ${patientName},</div><div>&nbsp;</div><div>Please find your latest patient statement attached to this email for your review.</div><div>&nbsp;</div><div>If you have any questions regarding your statement, please contact our office directly.</div>`;

export const getInvoiceDefaultEmailContent = (params: {
  patientName: string;
  practiceName: string;
  clinicPhone: string;
  attachmentName?: string;
}) => {
  const {
    patientName,
    practiceName,
    clinicPhone,
    attachmentName = "invoice"
  } = params;

  const phone = `If you have any questions please call us at ${formatPhoneNumber(
    clinicPhone || "",
    "parenthesis"
  )}`;
  return `<div>Dear ${patientName}. <br />Please find attached your ${attachmentName} from ${practiceName}.<br />${
    clinicPhone ? phone : ""
  }</div><div>&nbsp;</div><div>Best regards,</div><div>${practiceName}</div>`;
};

/** Converts SUNO merge tag placeholders to {{tag}} so backend can replace with patient/clinic values */
export const normalizeMergeTagsForSend = convertSunoPlaceholdersToMergeTags;

/**
 * Resolves every merge tag in the email builder's exported HTML, for the New Email modal's send.
 *
 * Resolution has to happen client-side here. `POST /emails/` never runs the backend's
 * `format_template` — `EmailSerializer.create` renders `PhysicianRecipient` tags and nothing else —
 * so any token left in the body reaches the recipient's inbox verbatim. There is no "leave it for
 * the server" option on this surface, with or without a patient.
 *
 * With no patient the map still fills the clinic fields and blanks the patient ones, which is the
 * closest to correct this surface can produce. That case is reachable: the designer tab's merge-tag
 * picker is not gated on having a patient the way the classic editor's is.
 *
 * Lives here rather than inline in `email-form.component.tsx` so it is covered — nothing in the
 * suite compiles or executes that component, so a mistake in this logic would reach production
 * unseen. `email.utils.ts` is exercised directly by `__tests__/`.
 */
export const resolveBuilderHtmlForSend = ({
  html,
  formPatient,
  fetchedPatient,
  preferredClinic,
  fallbackClinic
}: {
  html: string;
  formPatient: Patient | null | undefined;
  /** The lazily fetched patient, used when the form field holds only a stub. */
  fetchedPatient: Patient | null | undefined;
  /**
   * The patient's preferred clinic, fetched in full. The patient's own `preferred_clinic` is a
   * slim projection with no phone, address or practice, so those tags would send blank.
   */
  preferredClinic: Clinic | null | undefined;
  /** The clinic to fall back to when the patient has no preferred clinic. */
  fallbackClinic: Clinic | null | undefined;
}): string => {
  const patient = formPatient ?? fetchedPatient ?? null;
  const slimClinic = patient?.preferred_clinic;
  // RTK Query keeps the previous response while a new id loads, so match on the id.
  const clinic = slimClinic
    ? Number(preferredClinic?.id) === Number(slimClinic.id)
      ? preferredClinic
      : slimClinic
    : (fallbackClinic ?? null);

  return replaceMergeTagsWithValues(
    html,
    getMergeTagValuesMap(patient, clinic)
  );
};

export const getEmailRequestBody = ({
  recipients,
  content,
  subject,
  files,
  patient,
  applyBranding,
  email_design_json,
  reply_to,
  senderAddress
}: EmailFormValues & {
  isTemplateUsed: boolean;
  /**
   * The "Include email branding" checkbox. Ticked sends the fragment for the backend to brand;
   * unticked sends it in a blank document, which the backend never wraps. Undefined (a backend
   * without email branding) sends as before the feature: blank document, no `apply_branding` key.
   */
  applyBranding?: boolean;
  email_design_json?: any;
  /** The resolved sending (no-reply) address, used to skip a redundant Reply-To. */
  senderAddress?: string;
}) => {
  const normalizedRecipients = getNormalizedEmails(recipients);
  const replyToParticipant = getReplyToParticipant(reply_to, senderAddress);

  const html = normalizeMergeTagsForSend(
    applyBranding ? content : wrapWithBlankHtml(content)
  );

  return {
    subject,
    patient: patient?.id,
    participants: [
      ...normalizedRecipients.map((recipient) => ({
        type: RECIPIENT_DEFAULT_TYPE,
        object_id: typeof recipient === "string" ? undefined : recipient.id,
        address:
          typeof recipient === "string"
            ? recipient.trim()
            : recipient.email.trim(),
        object_type: typeof recipient === "string" ? undefined : recipient.type,
        name: typeof recipient === "string" ? undefined : recipient.name
      })),
      ...(replyToParticipant ? [replyToParticipant] : [])
    ],
    html,
    should_send: !files.length,
    mass_mailing: true,
    ...(applyBranding !== undefined && { apply_branding: applyBranding }),
    ...(email_design_json && { email_design_json })
  };
};

export const wrapWithBlankHtml = (content: string) => {
  return `<!DOCTYPE html> <html xmlns="http://www.w3.org/1999/xhtml"> <head> <meta http-equiv="Content-Type" content="text/html; charset=utf-8" /> <meta name="viewport" content="width=device-width" /><style>${DEFAULT_PDF_CSS_STYLE.replaceAll(
    "LatoRegular",
    "Helvetica, Arial, sans-serif"
  )} img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; } </style></head><body>${getEmailPreviewString(
    content
  )} ${normalizePrintableHtml(content)}</body></html>`;
};

export const getEmailPreviewString = (content: string) => {
  return `<div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;margin-right:100%;">${truncate(
    convertHtmlToText(content),
    {
      separator: "…",
      length: 1024
    }
  )}</div>`;
};

/**
 * The patient the current page is about, for the form's fallback association when the caller
 * passed no patient and no recipient: a patient chart (`/patients/:id/...`) or an inbox thread
 * (`/inbox/:id`). Read from the URL rather than `useParams()` because `EmailProvider` is mounted
 * above `<Routes />` (ENG-1988), where `useParams()` is always empty.
 */
export const getRoutePatientId = (pathname: string): number | undefined => {
  const id =
    matchPath("/patients/:patientId/*", pathname)?.params.patientId ??
    matchPath("/inbox/:patientId/*", pathname)?.params.patientId;
  return Number(id) || undefined;
};
